import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import StorageFileLink from "../components/StorageFileLink";
import { formatMoney as formatCurrency } from "../lib/currency";
import { supabase } from "../lib/supabase";
import { logActivity } from "../lib/auditLog";
import { buildStorageReference } from "../lib/storageReferences";
import { useToast } from "../contexts/ToastContext";
import {
  AlertCircle,
  CheckCircle2,
  Pencil,
  Plus,
  ReceiptText,
  Search,
  Upload,
  Trash2,
  Wallet,
  X,
} from "lucide-react";

const expensesTableGridClass =
  "md:grid-cols-[1fr_0.75fr_0.75fr_0.65fr_0.75fr_0.65fr_0.7fr_7.5rem]";

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`flex w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`flex w-full rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 ${className}`}
    >
      {children}
    </select>
  );
}

function Badge({ children, className = "" }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}

function formatMoney(amount, currency) {
  return formatCurrency(amount, currency);
}

function buildExpenseId(count) {
  const year = new Date().getFullYear();
  return `EXP-${year}-${String(count + 1).padStart(4, "0")}`;
}

const EXPENSE_DOCUMENT_BUCKET = "expense-documents";

function parseFileLinks(value) {
  if (!value) return [];

  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return String(value)
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
      .map((url) => ({ name: url.split("/").pop(), url }));
  }
}

function getExpenseFilePath(file) {
  const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "-");
  return `expenses/${Date.now()}-${safeName}`;
}

async function uploadExpenseFiles(files) {
  const uploadedFiles = [];

  for (const file of files) {
    const path = getExpenseFilePath(file);
    const { error } = await supabase.storage
      .from(EXPENSE_DOCUMENT_BUCKET)
      .upload(path, file, {
        cacheControl: "3600",
        upsert: false,
      });

    if (error) throw error;

    uploadedFiles.push({
      name: file.name,
      url: buildStorageReference(EXPENSE_DOCUMENT_BUCKET, path),
      type: file.type,
      size: file.size,
    });
  }

  return uploadedFiles;
}

function statusClasses(status) {
  if (status === "Paid") return "bg-emerald-100 text-emerald-700";
  if (status === "Approved") return "bg-sky-100 text-sky-700";
  return "bg-amber-100 text-amber-800";
}

function ExpenseModal({ isOpen, expense, properties, vendors, expenseCount, onClose, onSaved, isDark, currency }) {
  const toast = useToast();
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    property_id: "",
    property_name: "",
    category: "Maintenance",
    vendor: "",
    amount: "",
    expense_date: today,
    status: "Pending",
    payment_method: "Bank Transfer",
    notes: "",
    supporting_files: "",
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (expense) {
      setForm({
        property_id: expense.property_id || "",
        property_name: expense.property_name || "",
        category: expense.category || "Maintenance",
        vendor: expense.vendor || "",
        amount: expense.amount ?? "",
        expense_date: expense.expense_date || today,
        status: expense.status || "Pending",
        payment_method: expense.payment_method || "Bank Transfer",
        notes: expense.notes || "",
        supporting_files: expense.supporting_files || "",
      });
    } else {
      setForm({
        property_id: "",
        property_name: "",
        category: "Maintenance",
        vendor: "",
        amount: "",
        expense_date: today,
        status: "Pending",
        payment_method: "Bank Transfer",
        notes: "",
        supporting_files: "",
      });
    }
    setSelectedFiles([]);
  }, [expense, isOpen, today]);

  if (!isOpen) return null;

  const inputClass = isDark
    ? "border-slate-700 bg-slate-950 text-white placeholder:text-slate-500"
    : "border-slate-200 bg-white text-slate-900";

  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handlePropertyChange = (propertyId) => {
    const property = properties.find((item) => item.id === propertyId);
    setForm((prev) => ({
      ...prev,
      property_id: propertyId,
      property_name: property?.name || "",
    }));
  };

  const handleVendorChange = (vendorName) => {
    const vendor = vendors.find((item) => item.name === vendorName);
    setForm((prev) => ({
      ...prev,
      vendor: vendorName,
      category: vendor?.service_category || prev.category,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    let uploadedFiles = [];

    try {
      uploadedFiles = selectedFiles.length ? await uploadExpenseFiles(selectedFiles) : [];
    } catch (error) {
      setSaving(false);
      console.error("Expense file upload error:", error);
      toast.error("Expense file upload failed", "Please check the selected files and try again.");
      return;
    }

    const existingFiles = parseFileLinks(form.supporting_files);
    const supportingFiles = [...existingFiles, ...uploadedFiles];

    const payload = {
      property_id: form.property_id || null,
      property_name: form.property_name,
      category: form.category,
      vendor: form.vendor,
      amount: Number(form.amount || 0),
      expense_date: form.expense_date,
      status: form.status,
      payment_method: form.payment_method,
      notes: form.notes,
      supporting_files: supportingFiles.length ? JSON.stringify(supportingFiles) : null,
    };

    let result;

    if (expense?.id) {
      result = await supabase.from("expenses").update(payload).eq("id", expense.id).select().single();
    } else {
      result = await supabase
        .from("expenses")
        .insert([{ id: buildExpenseId(expenseCount), ...payload }])
        .select()
        .single();
    }

    setSaving(false);

    if (result.error) {
      console.error("Save expense error:", result.error);
      toast.error("Expense save failed", "Please review the expense details and try again.");
      return;
    }

    await logActivity({
      action: expense?.id ? "Updated expense" : "Created expense",
      entityType: "expense",
      entityId: result.data.id,
      entityLabel: `${result.data.property_name} - ${formatMoney(Number(result.data.amount || 0), currency)}`,
      details: payload,
    });

    toast.success(
      expense?.id ? "Expense updated" : "Expense created",
      `${result.data.property_name} now has an expense record in SpaceM8.`
    );
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className={`w-full max-w-2xl rounded-[2rem] border p-6 shadow-2xl ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h2 className={`text-2xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>
              {expense?.id ? "Edit Expense" : "Add Expense"}
            </h2>
            <p className={`mt-1 text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Track operating costs, vendors, and property-level spend.
            </p>
          </div>
          <button
            onClick={onClose}
            className={`rounded-2xl border p-3 transition ${isDark ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-4 md:grid-cols-2">
          <Select value={form.property_id} onChange={(event) => handlePropertyChange(event.target.value)} className={inputClass} required>
            <option value="">Select property</option>
            {properties.map((property) => (
              <option key={property.id} value={property.id}>
                {property.name}
              </option>
            ))}
          </Select>

          <Select value={form.category} onChange={(event) => update("category", event.target.value)} className={inputClass}>
            <option>Maintenance</option>
            <option>Utilities</option>
            <option>Security</option>
            <option>Cleaning</option>
            <option>Insurance</option>
            <option>Management</option>
            <option>Other</option>
          </Select>

          <Select value={form.vendor} onChange={(event) => handleVendorChange(event.target.value)} className={inputClass}>
            <option value="">Select vendor</option>
            <option value="In-house Team">In-house Team</option>
            {vendors.map((vendor) => (
              <option key={vendor.id} value={vendor.name}>
                {vendor.name} - {vendor.service_category}
              </option>
            ))}
          </Select>
          <Input type="number" value={form.amount} onChange={(event) => update("amount", event.target.value)} placeholder={`Amount (${currency})`} className={inputClass} required />

          <Input type="date" value={form.expense_date} onChange={(event) => update("expense_date", event.target.value)} className={inputClass} required />

          <Select value={form.status} onChange={(event) => update("status", event.target.value)} className={inputClass}>
            <option>Pending</option>
            <option>Approved</option>
            <option>Paid</option>
          </Select>

          <Select value={form.payment_method} onChange={(event) => update("payment_method", event.target.value)} className={inputClass}>
            <option>Bank Transfer</option>
            <option>Mobile Money</option>
            <option>Cash</option>
            <option>Card</option>
          </Select>

          <textarea
            value={form.notes}
            onChange={(event) => update("notes", event.target.value)}
            placeholder="Notes"
            className={`min-h-[110px] rounded-2xl border px-4 py-3 text-sm outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100 md:col-span-2 ${inputClass}`}
          />

          <div className={`rounded-2xl border p-4 md:col-span-2 ${isDark ? "border-slate-700 bg-slate-950" : "border-slate-200 bg-slate-50"}`}>
            <label className={`mb-2 block text-sm font-medium ${isDark ? "text-slate-300" : "text-slate-700"}`}>
              Supporting files
            </label>
            <input
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
              onChange={(event) => setSelectedFiles(Array.from(event.target.files || []))}
              className={`block w-full text-sm ${isDark ? "text-slate-300" : "text-slate-700"}`}
            />
            <p className={`mt-2 text-xs ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              Attach supplier invoices, receipts, photos, PDFs, or spreadsheets.
            </p>

            {parseFileLinks(form.supporting_files).length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {parseFileLinks(form.supporting_files).map((file) => (
                  <StorageFileLink
                    key={file.url}
                    reference={file.url}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-700"
                  >
                    {file.name || "Attachment"}
                  </StorageFileLink>
                ))}
              </div>
            ) : null}

            {selectedFiles.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {selectedFiles.map((file) => (
                  <span
                    key={`${file.name}-${file.size}`}
                    className="inline-flex items-center rounded-full bg-orange-100 px-3 py-1 text-xs font-semibold text-orange-700"
                  >
                    <Upload className="mr-1 h-3 w-3" />
                    {file.name}
                  </span>
                ))}
              </div>
            ) : null}
          </div>

          <div className="mt-2 flex items-center justify-end gap-3 md:col-span-2">
            <button type="button" onClick={onClose} className={`inline-flex h-12 items-center rounded-2xl border px-5 font-medium transition ${isDark ? "border-slate-700 bg-slate-900 text-white hover:bg-slate-800" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
              Cancel
            </button>
            <button type="submit" disabled={saving} className={`inline-flex h-12 items-center rounded-2xl px-5 font-medium text-white transition ${saving ? "bg-orange-300" : "bg-orange-500 hover:bg-orange-600"}`}>
              {saving ? "Saving..." : "Save Expense"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Expenses({ isDark, setIsDark, currency, setCurrency }) {
  const toast = useToast();
  const [expenses, setExpenses] = useState([]);
  const [properties, setProperties] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  const fetchExpenses = async () => {
    setLoading(true);

    const [
      { data: expenseData, error: expenseError },
      { data: propertyData, error: propertyError },
      { data: vendorData, error: vendorError },
    ] =
      await Promise.all([
        supabase.from("expenses").select("*").order("expense_date", { ascending: false }),
        supabase.from("properties").select("id, name").order("name", { ascending: true }),
        supabase
          .from("vendors")
          .select("id, name, service_category, status")
          .neq("status", "Inactive")
          .order("name", { ascending: true }),
      ]);

    if (expenseError) {
      console.error("Error fetching expenses:", expenseError);
      setExpenses([]);
    } else {
      setExpenses(expenseData || []);
    }

    if (propertyError) {
      console.error("Error fetching properties for expenses:", propertyError);
      setProperties([]);
    } else {
      setProperties(propertyData || []);
    }

    if (vendorError) {
      console.error("Error fetching vendors for expenses:", vendorError);
      setVendors([]);
    } else {
      setVendors(vendorData || []);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const totals = useMemo(() => {
    const paid = expenses
      .filter((expense) => expense.status === "Paid")
      .reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
    const pending = expenses
      .filter((expense) => expense.status !== "Paid")
      .reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
    const maintenance = expenses
      .filter((expense) => expense.category === "Maintenance")
      .reduce((sum, expense) => sum + Number(expense.amount || 0), 0);

    return { paid, pending, maintenance, total: expenses.length };
  }, [expenses]);

  const filteredExpenses = useMemo(() => {
    return expenses.filter((expense) => {
      const haystack = `${expense.property_name} ${expense.category} ${expense.vendor} ${expense.status}`.toLowerCase();
      const matchesQuery = haystack.includes(query.toLowerCase());
      const matchesStatus = statusFilter === "All" || expense.status === statusFilter;
      return matchesQuery && matchesStatus;
    });
  }, [expenses, query, statusFilter]);

  const handleDelete = async (expense) => {
    const confirmed = window.confirm(`Delete expense ${expense.id}?`);
    if (!confirmed) return;

    const { error } = await supabase.from("expenses").delete().eq("id", expense.id);

    if (error) {
      console.error("Delete expense error:", error);
      toast.error("Expense deletion failed", "Please try again in a moment.");
      return;
    }

    await logActivity({
      action: "Deleted expense",
      entityType: "expense",
      entityId: expense.id,
      entityLabel: `${expense.property_name} - ${expense.amount}`,
      details: expense,
    });

    toast.success("Expense deleted", `${expense.id} has been removed from SpaceM8.`);
    fetchExpenses();
  };

  return (
    <AppLayout
      isDark={isDark}
      setIsDark={setIsDark}
      currency={currency}
      setCurrency={setCurrency}
      eyebrow="Cost Control"
      title="Expenses"
      description="Track operating costs, vendor bills, maintenance spend, and property-level profitability."
      actions={
        <button
          onClick={() => {
            setEditingExpense(null);
            setIsModalOpen(true);
          }}
          className="inline-flex h-10 items-center justify-center rounded-xl bg-orange-500 px-3 text-sm font-medium text-white transition hover:bg-orange-600"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Expense
        </button>
      }
    >
      <section className="grid gap-4 md:grid-cols-2 2xl:grid-cols-4">
        {[
          { label: "Paid Expenses", value: formatMoney(totals.paid, currency), icon: CheckCircle2, tone: "bg-emerald-100 text-emerald-700" },
          { label: "Pending Spend", value: formatMoney(totals.pending, currency), icon: AlertCircle, tone: "bg-amber-100 text-amber-700" },
          { label: "Maintenance Spend", value: formatMoney(totals.maintenance, currency), icon: ReceiptText, tone: "bg-sky-100 text-sky-700" },
          { label: "Records", value: `${totals.total}`, icon: Wallet, tone: "bg-orange-100 text-orange-700" },
        ].map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.label} className={`rounded-[2rem] border p-5 shadow-md ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
              <div className="flex items-start justify-between">
                <div>
                  <p className={`text-sm ${isDark ? "text-slate-400" : "text-slate-500"}`}>{item.label}</p>
                  <h3 className={`mt-3 text-3xl font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{item.value}</h3>
                </div>
                <div className={`rounded-2xl p-3 ${item.tone}`}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <section className={`rounded-[2rem] border p-4 shadow-sm md:p-5 ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <div className="relative w-full max-w-md">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search property, vendor, category..."
              className={`h-12 pl-11 pr-4 ${isDark ? "border-slate-700 bg-slate-950 text-white" : "border-slate-200 bg-white text-slate-800"}`}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {["All", "Pending", "Approved", "Paid"].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`rounded-2xl px-4 py-2 text-sm font-medium transition ${
                  statusFilter === status
                    ? "bg-sky-100 text-sky-700"
                    : isDark
                    ? "bg-slate-800 text-slate-300 hover:bg-slate-700"
                    : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className={`overflow-hidden rounded-[2rem] border shadow-sm ${isDark ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
        <div className={`hidden items-center gap-4 px-5 py-4 text-sm md:grid ${expensesTableGridClass} ${isDark ? "bg-slate-950 text-slate-400" : "bg-slate-50 text-slate-600"}`}>
          <span>Property / Vendor</span>
          <span>Category</span>
          <span>Amount</span>
          <span>Date</span>
          <span>Method</span>
          <span>Status</span>
          <span>Files</span>
          <span className="text-center">Actions</span>
        </div>

        {loading ? (
          <div className={`p-8 text-center ${isDark ? "text-slate-300" : "text-slate-600"}`}>Loading expenses...</div>
        ) : filteredExpenses.length === 0 ? (
          <div className={`p-8 text-center ${isDark ? "text-slate-300" : "text-slate-600"}`}>No expenses found.</div>
        ) : (
          filteredExpenses.map((expense) => (
            <div key={expense.id} className={`grid items-center gap-4 border-t px-5 py-4 text-sm ${expensesTableGridClass} ${isDark ? "border-slate-800" : "border-slate-200"}`}>
              <div>
                <p className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{expense.property_name}</p>
                <p className={isDark ? "text-slate-400" : "text-slate-500"}>{expense.vendor || expense.id}</p>
              </div>
              <span className={isDark ? "text-slate-300" : "text-slate-600"}>{expense.category}</span>
              <span className={`font-semibold ${isDark ? "text-white" : "text-slate-900"}`}>{formatMoney(Number(expense.amount || 0), currency)}</span>
              <span className={isDark ? "text-slate-300" : "text-slate-600"}>{expense.expense_date}</span>
              <span className={isDark ? "text-slate-300" : "text-slate-600"}>{expense.payment_method}</span>
              <Badge className={statusClasses(expense.status)}>{expense.status}</Badge>
              <div className="flex flex-wrap gap-1">
                {parseFileLinks(expense.supporting_files).length === 0 ? (
                  <span className={isDark ? "text-slate-500" : "text-slate-400"}>None</span>
                ) : (
                  parseFileLinks(expense.supporting_files).slice(0, 2).map((file, index) => (
                    <StorageFileLink
                      key={`${file.url}-${index}`}
                      reference={file.url}
                      target="_blank"
                      rel="noreferrer"
                      className="rounded-full bg-sky-100 px-2 py-1 text-[10px] font-semibold text-sky-700"
                    >
                      File {index + 1}
                    </StorageFileLink>
                  ))
                )}
              </div>
              <div className="flex items-center justify-center gap-2">
                <button
                  onClick={() => {
                    setEditingExpense(expense);
                    setIsModalOpen(true);
                  }}
                  className={`rounded-xl border p-2 transition ${isDark ? "border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-white" : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-slate-700"}`}
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(expense)}
                  className={`rounded-xl border p-2 transition ${isDark ? "border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-red-400" : "border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-red-600"}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </section>

      <ExpenseModal
        isOpen={isModalOpen}
        expense={editingExpense}
        properties={properties}
        vendors={vendors}
        expenseCount={expenses.length}
        onClose={() => {
          setIsModalOpen(false);
          setEditingExpense(null);
        }}
        onSaved={fetchExpenses}
        isDark={isDark}
        currency={currency}
      />
    </AppLayout>
  );
}
